import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useScreenSize } from 'hooks/useScreenSize';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  NAVIGATE_MODAL,
  doSetupSsoClose,
  doSetupSsoUpdate,
  doToast,
} from 'store/actions';
import {
  selectRemoteOrganisationId,
  selectSsoSettings,
} from 'store/remote/selectors';
import * as Clipboard from 'expo-clipboard';
import { Button } from 'components/Button';
import { openUrl } from 'lib/riskbase';
import { SCIM_API_DOCS_WEBSITE } from 'types/types';
import { PageHeader } from 'components/PageHeader';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsSso
>;

export const SsoSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisationId = useSelector(selectRemoteOrganisationId);
  const ssoSettings = useSelector(selectSsoSettings);

  React.useEffect(() => {
    return () => {
      dispatch(doSetupSsoClose());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!organisationId) {
      navigation.goBack();
    }
  }, [navigation, organisationId]);

  if (!organisationId || !ssoSettings) {
    return null;
  }

  const handleCopySpEntityId = async () => {
    const copied = await Clipboard.setStringAsync(ssoSettings.samlSpEntityId);
    if (copied) {
      dispatch(doToast('Entity ID Copied'));
    }
  };
  const handleCopySpAcsUrl = async () => {
    const copied = await Clipboard.setStringAsync(ssoSettings.samlSpAcsUrl);
    if (copied) {
      dispatch(doToast('ACS URL Copied'));
    }
  };
  const handleCopyScimApiUrl = async () => {
    const copied = await Clipboard.setStringAsync(ssoSettings.scimApiUrl);
    if (copied) {
      dispatch(doToast('SCIM API URL Copied'));
    }
  };

  const handleToggleSaml = () => {
    dispatch(
      doSetupSsoUpdate({
        organisationId,
        samlEnabled: !ssoSettings.samlEnabled,
      }),
    );
  };
  const handleToggleScim = () => {
    dispatch(
      doSetupSsoUpdate({
        organisationId,
        scimEnabled: !ssoSettings.scimEnabled,
      }),
    );
  };

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="SSO & Provisioning"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      <Card label="Single Sign-On with SAML">
        <CardRow
          label="Enable SAML"
          checkedValue={ssoSettings.samlEnabled}
          onPress={handleToggleSaml}
        />
      </Card>

      <Card
        label="Identity Provider Settings"
        labelLinkText="Edit Settings"
        onLabelLinkPress={() =>
          dispatch(
            NAVIGATE_MODAL({
              screen: ModalScreens.SettingsSsoIdp,
              params: {},
            }),
          )
        }
      >
        <CardRow
          label="Entity ID"
          value={ssoSettings.samlIdpEntityId}
          placeholder="No IdP Entity ID"
          labelAlignTop
        />
        <CardRow
          label="Certificate"
          valueNumberOfLines={2}
          value={ssoSettings.samlIdpCertificate}
          placeholder="No IdP Certificate"
          labelAlignTop
        />
      </Card>

      <Card label="Service Provider Settings">
        <CardRow
          label="Entity ID"
          value={ssoSettings.samlSpEntityId}
          labelAlignTop
          actionIcon={ICONS.CopyLight}
          onAction={handleCopySpEntityId}
        />
        <CardRow
          label="Assertion Consumer Service URL"
          value={ssoSettings.samlSpAcsUrl}
          labelAlignTop
          actionIcon={ICONS.CopyLight}
          onAction={handleCopySpAcsUrl}
        />
      </Card>

      <SeparatorHorizontal />

      <Card label="Provisioning with SCIM">
        <CardRow
          label="Enable SCIM Provisioning"
          checkedValue={ssoSettings.scimEnabled}
          onPress={handleToggleScim}
        />
      </Card>

      <Card label="Service Provider Settings">
        <CardRow
          label="SCIM API"
          value={ssoSettings.scimApiUrl}
          labelAlignTop
          actionIcon={ICONS.CopyLight}
          onAction={handleCopyScimApiUrl}
        />
      </Card>
      <Card explanation="See our SCIM API Documentation for a detailed look at our implementation" />
      <Button
        text="SCIM API Documentation"
        onPress={() => openUrl(SCIM_API_DOCS_WEBSITE)}
        style={{ alignSelf: 'center' }}
      />
    </Page>
  );
};
